// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/utils';

export interface AppFavoriteToggleProps {
  appId: string;
  appName: string;
  favorite: boolean;
  className?: string;
  /**
   * Query key prefix to invalidate after the toggle. Defaults to the apps list;
   * the marketplace grid passes its own so its cards re-read the new tier.
   */
  invalidateKey?: readonly unknown[];
}

/**
 * Per-row favorite star (docs/28 §/apps Personalization, T-7557) — pure
 * personal view state, never audited. Shared by the table row, mobile card,
 * and grid card so the toggle behaves identically everywhere it appears.
 * Filled vs. outline is a shape change, not just a color change, so the
 * pressed state reads without relying on color alone.
 */
export function AppFavoriteToggle({
  appId,
  appName,
  favorite,
  className,
  invalidateKey = ['apps'],
}: AppFavoriteToggleProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const setFavorite = api.apps.setFavorite.useMutation();
  const unsetFavorite = api.apps.unsetFavorite.useMutation();
  const pending = setFavorite.isPending || unsetFavorite.isPending;
  const label = favorite ? `Unfavorite ${appName}` : `Favorite ${appName}`;

  const onClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (pending) return;
    try {
      const res = favorite
        ? await unsetFavorite.mutateAsync({ params: { id: appId } })
        : await setFavorite.mutateAsync({ params: { id: appId } });
      if (res.status >= 300) throw res;
      // A prefix key matches every paginated [key, cursor] query.
      await queryClient.invalidateQueries({ queryKey: [...invalidateKey] });
    } catch (err) {
      toast.error(`Couldn't update favorite: ${mutationErrorMessage(err)}`);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('h-11 w-11 md:h-8 md:w-8', className)}
            aria-pressed={favorite}
            aria-label={label}
            disabled={pending}
            onClick={(e) => void onClick(e)}
          >
            <Star
              className={cn(
                'size-4',
                favorite ? 'fill-primary text-primary' : 'text-muted-foreground',
              )}
              aria-hidden
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
